import { debounce, fetchConfig } from "@theme/utilities";
import { loadVehicle, stripKenteken } from "@theme/kenteken-api";
//#region assets/kenteken-cart.ts
/**
* Syncs the session vehicle plate onto cart attribute `kenteken`.
*
* - Fills every `[data-vp-kenteken-cart-attr]` hidden input (add-to-cart forms)
* - Debounced POST to /cart/update.js so Shop Pay / Direct afrekenen / sticky ATC
*   also carry the plate without relying on a single product form
*
* Only a real plate/VIN is sent. Model-only selection leaves the attribute empty.
*/
var ATTR_SELECTOR = "[data-vp-kenteken-cart-attr]";
/** @type {string} */
var lastSynced = "__unset__";
/** @type {AbortController | null} */
var activeFetch = null;
/**
* @param {import('@theme/kenteken-api').VpVehicle | null | undefined} vehicle
* @returns {string}
*/
function plateFromVehicle(vehicle) {
	if (!vehicle?.kenteken) return "";
	return stripKenteken(vehicle.kenteken).toUpperCase();
}
/**
* @param {string} value
*/
function fillHiddenFields(value) {
	document.querySelectorAll(ATTR_SELECTOR).forEach((el) => {
		if (el instanceof HTMLInputElement) el.value = value;
	});
}
/**
* @param {string} kenteken
*/
async function postCartAttribute(kenteken) {
	const updateUrl = Theme?.routes?.cart_update_url;
	const cartUrl = Theme?.routes?.cart_url;
	if (!updateUrl || !cartUrl) return;
	if (activeFetch) activeFetch.abort();
	const abortController = new AbortController();
	activeFetch = abortController;
	try {
		const cart = await (await fetch(`${cartUrl}.js`, {
			signal: abortController.signal,
			headers: { Accept: "application/json" }
		})).json();
		const existing = cart?.attributes && typeof cart.attributes === "object" ? { ...cart.attributes } : {};
		if ((existing.kenteken || "") === kenteken) return;
		const config = fetchConfig("json", { body: JSON.stringify({ attributes: {
			...existing,
			kenteken
		} }) });
		await fetch(updateUrl, {
			...config,
			signal: abortController.signal
		});
	} catch (error) {
		if (error instanceof Error && error.name === "AbortError") return;
		console.warn("[kenteken-cart] Failed to sync cart attribute", error);
	} finally {
		if (activeFetch === abortController) activeFetch = null;
	}
}
/**
* @param {import('@theme/kenteken-api').VpVehicle | null | undefined} vehicle
* @param {{ updateCart?: boolean }} [options]
*/
function syncFromVehicle(vehicle, options = {}) {
	const updateCart = options.updateCart !== false;
	const kenteken = plateFromVehicle(vehicle);
	fillHiddenFields(kenteken);
	if (kenteken === lastSynced) return;
	lastSynced = kenteken;
	if (updateCart) scheduleCartUpdate(kenteken);
}
var scheduleCartUpdate = debounce((kenteken) => {
	postCartAttribute(kenteken);
}, 300);
function init() {
	const kenteken = plateFromVehicle(loadVehicle());
	fillHiddenFields(kenteken);
	lastSynced = kenteken;
	if (kenteken) scheduleCartUpdate(kenteken);
	window.addEventListener("vp-vehicle:change", (event) => {
		const detail = event.detail;
		syncFromVehicle(detail?.vehicle ?? null, { updateCart: true });
	});
	new MutationObserver((mutations) => {
		for (const mutation of mutations) for (const node of mutation.addedNodes) {
			if (!(node instanceof HTMLElement)) continue;
			if (!(node.matches?.(ATTR_SELECTOR) || Boolean(node.querySelector?.(ATTR_SELECTOR)))) continue;
			fillHiddenFields(lastSynced === "__unset__" ? "" : lastSynced);
			return;
		}
	}).observe(document.documentElement, {
		childList: true,
		subtree: true
	});
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
else init();
//#endregion
